const option = (value: string, label: string) => ({ value, label })
const radiusOptions = [option('none', 'Square'), option('sm', 'Subtle'), option('md', 'Rounded'), option('lg', 'Large')]
const borderOptions = [option('none', 'None'), option('subtle', 'Subtle'), option('strong', 'Strong')]
const elevationOptions = [option('none', 'None'), option('subtle', 'Subtle'), option('raised', 'Raised')]
const fontOptions = [
  option('avenir', 'Avenir'),
  option('inter', 'Inter'),
  option('lato', 'Lato'),
  option('montserrat', 'Montserrat'),
  option('roboto', 'Roboto'),
  option('system-sans', 'System sans'),
]

const colors = (
  primary: string,
  secondary: string,
  tertiary: string,
  light: readonly [string, string, string, string, string],
  dark: readonly [string, string, string, string, string]
) => ({
  light: {
    primary,
    secondary,
    tertiary,
    background: light[1],
    surface: light[0],
    sidebar: light[2],
    text: light[3],
    border: light[4],
    neutral: '#3d4451',
    info: '#0ba5ec',
    success: '#039855',
    warning: '#f79009',
    error: '#d92d20',
  },
  dark: {
    primary,
    secondary,
    tertiary,
    background: dark[0],
    surface: dark[1],
    sidebar: dark[2],
    text: dark[3],
    border: dark[4],
    neutral: '#4b5563',
    info: '#38bdf8',
    success: '#32d583',
    warning: '#fdb022',
    error: '#f97066',
  },
})

const componentTheme = (
  card: readonly [string, string, string],
  media: string,
  badge: string,
  button: readonly [string, string, string],
  field: readonly [string, string],
  selector: string,
  overlay: readonly [string, string, string]
) => ({
  card: { radius: card[0], border: card[1], elevation: card[2] },
  media: { radius: media },
  badge: { radius: badge },
  button: { radius: button[0], border: button[1], elevation: button[2] },
  field: { radius: field[0], border: field[1] },
  selector: { radius: selector },
  overlay: { radius: overlay[0], border: overlay[1], elevation: overlay[2] },
})

const preset = (id: string, label: string, description: string, theme: Record<string, unknown>) => ({
  id,
  label,
  description,
  preview: {
    colors: Object.values((theme.colors as { light: Record<string, string> }).light).slice(0, 4),
    typeface: (theme.typography as { heading: string }).heading,
  },
  theme,
})

export const CLIENTPORTAL_DEFAULT_THEME_V2 = {
  colors: colors(
    '#155eef',
    '#344054',
    '#004eeb',
    ['#f8fafc', '#ffffff', '#ffffff', '#101828', '#e4e7ec'],
    ['#020617', '#0f172a', '#020617', '#f8fafc', '#334155']
  ),
  typography: { heading: 'montserrat', body: 'inter', scale: 'default' },
  density: 'comfortable',
  components: componentTheme(['lg', 'subtle', 'subtle'], 'lg', 'pill', ['md', 'subtle', 'none'], ['sm', 'subtle'], 'sm', [
    'lg',
    'subtle',
    'raised',
  ]),
  sizes: { fieldBase: 4, selectorBase: 4, borderWidth: 1 },
  effects: { noise: false },
  options: { defaultTheme: false, defaultDarkTheme: false, darkScheme: false },
} as const

const clientPortalPresets = [
  preset(
    'professional',
    'Professional',
    'Clean blue branding for services, coaching, and business portals.',
    CLIENTPORTAL_DEFAULT_THEME_V2
  ),
  preset('modern', 'Modern', 'Bright indigo with crisp typography for a contemporary digital feel.', {
    colors: colors(
      '#4f46e5',
      '#0891b2',
      '#7c3aed',
      ['#f8fafc', '#ffffff', '#ffffff', '#0f172a', '#e2e8f0'],
      ['#09090b', '#18181b', '#09090b', '#fafafa', '#3f3f46']
    ),
    typography: { heading: 'inter', body: 'inter', scale: 'default' },
    density: 'comfortable',
    components: componentTheme(['md', 'subtle', 'raised'], 'md', 'pill', ['lg', 'none', 'raised'], ['md', 'strong'], 'pill', [
      'md',
      'subtle',
      'raised',
    ]),
  }),
  preset('warm', 'Warm', 'Welcoming earth tones for wellness, hospitality, and personal brands.', {
    colors: colors(
      '#c2410c',
      '#d97706',
      '#b45309',
      ['#fffbeb', '#fff7ed', '#fffdf8', '#431407', '#fed7aa'],
      ['#1c1917', '#292524', '#1c1917', '#fafaf9', '#57534e']
    ),
    typography: { heading: 'montserrat', body: 'system-sans', scale: 'large' },
    density: 'roomy',
    components: componentTheme(['lg', 'subtle', 'subtle'], 'lg', 'pill', ['pill', 'none', 'subtle'], ['lg', 'subtle'], 'pill', [
      'lg',
      'subtle',
      'subtle',
    ]),
  }),
  preset('natural', 'Natural', 'Fresh greens and calm spacing for health, fitness, and community.', {
    colors: colors(
      '#047857',
      '#65a30d',
      '#0f766e',
      ['#f0fdf4', '#ffffff', '#f7fee7', '#052e16', '#bbf7d0'],
      ['#052e16', '#14532d', '#052e16', '#f0fdf4', '#166534']
    ),
    typography: { heading: 'inter', body: 'inter', scale: 'default' },
    density: 'roomy',
    components: componentTheme(['lg', 'subtle', 'subtle'], 'lg', 'pill', ['lg', 'subtle', 'none'], ['lg', 'subtle'], 'pill', [
      'lg',
      'subtle',
      'subtle',
    ]),
  }),
  preset('bold', 'Bold', 'Expressive violet and pink for creators, launches, and vibrant brands.', {
    colors: colors(
      '#7c3aed',
      '#db2777',
      '#2563eb',
      ['#faf5ff', '#ffffff', '#fdf4ff', '#2e1065', '#e9d5ff'],
      ['#1e1b4b', '#2e1065', '#1e1b4b', '#faf5ff', '#6d28d9']
    ),
    typography: { heading: 'montserrat', body: 'inter', scale: 'large' },
    density: 'comfortable',
    components: componentTheme(['lg', 'strong', 'raised'], 'md', 'pill', ['pill', 'strong', 'raised'], ['md', 'strong'], 'pill', [
      'lg',
      'strong',
      'raised',
    ]),
  }),
  preset('minimal', 'Minimal', 'Quiet monochrome styling that keeps the focus on your content.', {
    colors: colors(
      '#18181b',
      '#52525b',
      '#71717a',
      ['#ffffff', '#fafafa', '#ffffff', '#18181b', '#d4d4d8'],
      ['#09090b', '#18181b', '#09090b', '#fafafa', '#3f3f46']
    ),
    typography: { heading: 'system-sans', body: 'system-sans', scale: 'compact' },
    density: 'compact',
    components: componentTheme(['sm', 'strong', 'none'], 'none', 'sm', ['none', 'strong', 'none'], ['none', 'strong'], 'none', [
      'sm',
      'strong',
      'none',
    ]),
  }),
] as const

const colorSeeds = [
  ['primary', 'Brand colour'],
  ['secondary', 'Supporting colour'],
  ['tertiary', 'Accent colour'],
  ['background', 'Page background'],
  ['surface', 'Section background'],
  ['sidebar', 'Navigation background'],
  ['text', 'Text colour'],
  ['border', 'Lines and borders'],
  ['neutral', 'Neutral colour'],
  ['info', 'Info colour'],
  ['success', 'Success'],
  ['warning', 'Warning'],
  ['error', 'Error'],
] as const
const colorControls = (mode: 'light' | 'dark') =>
  colorSeeds.map(([id, label]) => ({
    path: `theme.colors.${mode}.${id}`,
    label,
    control: 'color',
    allowInherit: false,
  }))
// On-colour (font) overrides: the text colour that renders on top of each
// coloured surface. Left inherited by default so the runtime auto-derives a
// readable on-colour (pickOnColor); authoring a value overrides that per family.
const contentSeeds = [
  ['primaryContent', 'Brand text colour'],
  ['secondaryContent', 'Supporting text colour'],
  ['tertiaryContent', 'Accent text colour'],
  ['neutralContent', 'Neutral text colour'],
  ['infoContent', 'Info text colour'],
  ['successContent', 'Success text colour'],
  ['warningContent', 'Warning text colour'],
  ['errorContent', 'Error text colour'],
] as const
const contentControls = (mode: 'light' | 'dark') =>
  contentSeeds.map(([id, label]) => ({
    path: `theme.colors.${mode}.${id}`,
    label,
    control: 'color',
    allowInherit: true,
  }))
const family = (id: string, label: string, description: string, controls: readonly Record<string, unknown>[]) => ({
  id,
  label,
  description,
  controls,
})
const segmented = (path: string, label: string, options: readonly { value: string; label: string }[]) => ({
  path,
  label,
  control: 'segmented',
  options,
})

export const CLIENTPORTAL_THEME_CONTRACT_V2 = {
  scope: 'location',
  controls: [],
  presets: clientPortalPresets,
  foundations: [
    {
      id: 'colors.light',
      label: 'Light mode colours',
      collapsed: false,
      controls: [...colorControls('light'), ...contentControls('light')],
    },
    { id: 'colors.dark', label: 'Dark mode colours', collapsed: true, controls: [...colorControls('dark'), ...contentControls('dark')] },
    {
      id: 'typography',
      label: 'Typography',
      collapsed: false,
      controls: [
        { path: 'theme.typography.heading', label: 'Heading font', control: 'select', options: fontOptions.slice(1) },
        { path: 'theme.typography.body', label: 'Text font', control: 'select', options: fontOptions.slice(1) },
        segmented('theme.typography.scale', 'Text size', [
          option('compact', 'Compact'),
          option('default', 'Default'),
          option('large', 'Large'),
        ]),
      ],
    },
    {
      id: 'density',
      label: 'Spacing density',
      collapsed: false,
      controls: [
        segmented('theme.density', 'Page spacing', [
          option('compact', 'Compact'),
          option('comfortable', 'Comfortable'),
          option('roomy', 'Roomy'),
        ]),
      ],
    },
  ],
  families: [
    family('card', 'Cards', 'Content cards and panels.', [
      segmented('theme.components.card.radius', 'Roundness', radiusOptions),
      segmented('theme.components.card.border', 'Border', borderOptions),
      segmented('theme.components.card.elevation', 'Depth', elevationOptions),
    ]),
    family('media', 'Images', 'Course covers and other images.', [segmented('theme.components.media.radius', 'Roundness', radiusOptions)]),
    family('badge', 'Labels and tags', 'Status labels, tags, and counters.', [
      segmented('theme.components.badge.radius', 'Roundness', [...radiusOptions, option('pill', 'Pill')]),
    ]),
    family('button', 'Buttons', 'Primary and secondary actions.', [
      segmented('theme.components.button.radius', 'Roundness', [...radiusOptions, option('pill', 'Pill')]),
      segmented('theme.components.button.border', 'Outline', borderOptions),
      segmented('theme.components.button.elevation', 'Depth', elevationOptions),
    ]),
    family('field', 'Form fields', 'Inputs, dropdowns, and form controls.', [
      segmented('theme.components.field.radius', 'Roundness', radiusOptions),
      segmented('theme.components.field.border', 'Outline', borderOptions),
    ]),
    family('selector', 'Tabs and selectors', 'Tabs, toggles, and checkboxes.', [
      segmented('theme.components.selector.radius', 'Roundness', [...radiusOptions, option('pill', 'Pill')]),
    ]),
    family('overlay', 'Modals and menus', 'Dialogs, popovers, and menus.', [
      segmented('theme.components.overlay.radius', 'Roundness', radiusOptions),
      segmented('theme.components.overlay.border', 'Outline', borderOptions),
      segmented('theme.components.overlay.elevation', 'Depth', elevationOptions),
    ]),
  ],
} as const
